import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLanguage } from "../language";
import logo from "../assets/IconMetuaa.png";

export const headerRideButtonStyle = {
  "--padding": "24px",
  "--min-height": "48px",
  "--min-width": "120px",
  color: "#ffffff",
};

export const footerAppButtonStyle = {
  "--padding": "16px",
  "--min-height": "32px",
  "--min-width": "100px",
};

export const IconLanguage = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 32 32"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M15.982 3C8.719 3 4.05 7.7 4.05 14.932c0 3.662 1.16 6.683 3.357 8.82L5.973 29l6.469-2.533c1.13.275 2.35.427 3.57.427 7.263 0 11.932-4.7 11.932-11.932S23.244 3 15.982 3zm0 22.308c-1.251 0-2.624-1.923-3.387-5.036h6.774c-.763 3.113-2.136 5.036-3.387 5.036zm-3.693-6.653a23.424 23.424 0 0 1-.274-3.723c0-1.343.091-2.594.274-3.723h7.385c.184 1.129.275 2.38.275 3.723s-.091 2.594-.275 3.723H12.29zm3.693-14.038c1.251 0 2.624 1.923 3.387 5.036h-6.774c.762-3.113 2.136-5.036 3.387-5.036zm5.31 6.622h4.516c.336 1.068.519 2.32.519 3.723 0 1.404-.183 2.625-.519 3.723h-4.516c.152-1.16.244-2.41.244-3.723 0-1.312-.061-2.563-.244-3.723zm3.875-1.586h-4.15c-.427-1.892-1.068-3.48-1.861-4.639 2.472.61 4.73 2.075 6.011 4.639zM12.808 5.014c-.793 1.16-1.434 2.747-1.861 4.639h-4.15c1.281-2.564 3.54-4.029 6.011-4.639zM6.156 11.24h4.516a28.605 28.605 0 0 0 0 7.446H6.156a13.066 13.066 0 0 1-.52-3.723c0-1.403.184-2.655.52-3.723zm6.164 13.55-3.998 1.556.855-3.113-.366-.335a8.99 8.99 0 0 1-2.015-2.625h4.15c.428 1.892 1.069 3.48 1.862 4.639-.091-.03-.152-.03-.244-.061l-.244-.061zm6.836.122c.793-1.16 1.434-2.747 1.861-4.639h4.15c-1.281 2.533-3.54 3.998-6.011 4.639z" />
  </svg>
);

export const LocaleSelector = () => {
  const rootRef = useRef(null);
  const intl = useIntl();
  const { locale: language, setLocale } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const options = [
    { labelId: "header.locale.french", value: "fr" },
    { labelId: "header.locale.english", value: "en" },
  ];

  const currentOption = options.find((option) => option.value === language) || options[0];
  const activeLabel = language.toUpperCase();
  const currentLanguageLabel = intl.formatMessage({ id: currentOption.labelId });

  return (
    <div className="site-language-selector" ref={rootRef}>
      <button
        type="button"
        className="site-language-selector__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={intl.formatMessage(
          { id: "header.locale.current" },
          { language: currentLanguageLabel }
        )}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="site-language-selector__icon" aria-hidden="true">
          <IconLanguage />
        </span>
        <span className="site-language-selector__value">{activeLabel}</span>
      </button>
      <button
        type="button"
        className="site-language-selector__caretButton"
        aria-label={intl.formatMessage({ id: "header.locale.open" })}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="site-language-selector__caret" aria-hidden="true">
          <IconChevronDown />
        </span>
      </button>
      {isOpen ? (
        <div
          className="site-language-selector__menu"
          role="listbox"
          aria-label={intl.formatMessage({ id: "header.locale.choose" })}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={language === option.value}
              className={`site-language-selector__option${language === option.value ? " is-active" : ""}`}
              onClick={() => {
                setLocale(option.value);
                setIsOpen(false);
              }}
            >
              {intl.formatMessage({ id: option.labelId })}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const IconHamburger = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M29 7.75H3v1.989h26V7.75zm0 7.26H3V17h26v-1.99zm0 7.238H3v1.989h26v-1.99z" />
  </svg>
);

export const IconChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7.988 12.11 1 5.098 2.208 3.89l5.78 5.78 5.78-5.78L15 5.098 7.988 12.11z" />
  </svg>
);

export const IconTwitter = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M5.403 13.376c5.283 0 8.172-4.377 8.172-8.173 0-.124 0-.248-.008-.371A5.844 5.844 0 0 0 15 3.345a5.732 5.732 0 0 1-1.65.452 2.882 2.882 0 0 0 1.263-1.589c-.564.335-1.18.57-1.824.697a2.875 2.875 0 0 0-4.895 2.62 8.155 8.155 0 0 1-5.92-3 2.874 2.874 0 0 0 .89 3.833 2.85 2.85 0 0 1-1.304-.36v.037c0 1.368.964 2.545 2.304 2.816a2.868 2.868 0 0 1-1.297.05 2.876 2.876 0 0 0 2.684 1.994A5.763 5.763 0 0 1 1 12.085a8.132 8.132 0 0 0 4.403 1.289" />
  </svg>
);

export const IconFacebook = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M15 8a7 7 0 1 0-8.094 6.915v-4.892H5.13V8h1.777V6.458c0-1.755 1.045-2.724 2.644-2.724.766 0 1.567.137 1.567.137v1.723h-.883c-.87 0-1.14.54-1.14 1.093V8h1.941l-.31 2.023H9.094v4.892A7.002 7.002 0 0 0 15 8z" clipRule="evenodd" fillRule="evenodd" />
  </svg>
);

export const IconInstagram = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ fill: "currentColor" }}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7.998 4.918a3.08 3.08 0 1 0 0 6.16 3.08 3.08 0 0 0 0-6.16zm0 5.08a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" clipRule="evenodd" fillRule="evenodd" />
    <path d="M11.2 5.516a.72.72 0 1 0 0-1.44.72.72 0 0 0 0 1.44z" />
    <path d="M7.998 2c-1.629 0-1.833.007-2.473.036-.638.03-1.074.13-1.456.279a2.94 2.94 0 0 0-1.062.692 2.94 2.94 0 0 0-.692 1.062c-.148.382-.25.818-.279 1.456-.03.64-.036.844-.036 2.473 0 1.63.007 1.833.036 2.473.03.639.13 1.075.279 1.456.153.395.358.729.692 1.063.333.333.668.538 1.062.691.382.149.818.25 1.456.28.64.028.844.035 2.473.035 1.63 0 1.833-.007 2.473-.036.639-.029 1.075-.13 1.456-.279a2.939 2.939 0 0 0 1.063-.691c.333-.334.538-.668.691-1.063.149-.381.25-.817.28-1.456.028-.64.035-.844.035-2.473s-.007-1.833-.036-2.473c-.029-.638-.13-1.074-.279-1.456a2.939 2.939 0 0 0-.691-1.062 2.94 2.94 0 0 0-1.063-.692c-.381-.148-.817-.25-1.456-.279C9.831 2.006 9.627 2 7.998 2zm0 1.08c1.602 0 1.791.007 2.424.036.585.026.902.124 1.114.206.28.11.48.239.69.449.21.21.34.41.448.69.082.211.18.529.207 1.113.028.633.034.823.034 2.424 0 1.602-.006 1.791-.034 2.424-.027.585-.125.902-.207 1.114-.109.28-.239.48-.449.69-.21.21-.41.34-.69.448-.21.082-.528.18-1.113.207-.633.028-.822.034-2.424.034-1.602 0-1.791-.006-2.424-.034-.584-.027-.902-.125-1.113-.207a1.858 1.858 0 0 1-.69-.449c-.21-.21-.34-.41-.449-.69-.082-.21-.18-.528-.206-1.113-.03-.633-.035-.822-.035-2.424 0-1.601.006-1.791.035-2.424.026-.584.124-.902.206-1.113.11-.28.239-.48.449-.69.21-.21.41-.34.69-.449.211-.082.529-.18 1.113-.206.633-.03.823-.035 2.424-.035z" clipRule="evenodd" fillRule="evenodd" />
  </svg>
);

export const IconPrivacyChoice = () => (
  <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 14" aria-hidden="true" focusable="false">
    <g fillRule="evenodd" clipRule="evenodd" fill="#fff">
      <path d="M7.4 12.8h6.8l3.1-11.6H7.4C4.2 1.2 1.6 3.8 1.6 7s2.6 5.8 5.8 5.8z" />
    </g>
    <g fillRule="evenodd" clipRule="evenodd" fill="#06f">
      <path d="M22.6 0H7.4c-3.9 0-7 3.1-7 7s3.1 7 7 7h15.2c3.9 0 7-3.1 7-7s-3.2-7-7-7zm-21 7c0-3.2 2.6-5.8 5.8-5.8h9.9l-3.1 11.6H7.4c-3.2 0-5.8-2.6-5.8-5.8z" />
    </g>
    <g fill="#fff">
      <path d="M24.6 4c.2.2.2.6 0 .8L22.5 7l2.2 2.2c.2.2.2.6 0 .8-.2.2-.6.2-.8 0l-2.2-2.2-2.2 2.2c-.2.2-.6.2-.8 0-.2-.2-.2-.6 0-.8L20.8 7l-2.2-2.2c-.2-.2-.2-.6 0-.8.2-.2.6-.2.8 0l2.2 2.2L23.8 4c.2-.2.6-.2.8 0z" />
    </g>
    <g fill="#06f">
      <path d="M12.7 4.1c.2.2.3.6.1.8L8.6 9.8c-.1.1-.2.2-.3.2-.2.1-.5.1-.7-.1L5.4 7.7c-.2-.2-.2-.6 0-.8.2-.2.6-.2.8 0L8 8.6l3.8-4.5c.2-.2.6-.2.9 0z" />
    </g>
  </svg>
);

export const footerDriverLinks = [
  { href: "https://www.lyft.com/drive-with-lyft", textId: "footer.driver.becomeDriver" },
  { href: "https://www.lyft.com/driver/new-driver-guide", textId: "footer.driver.newDriverGuide" },
  { href: "/driver/pay", textId: "footer.driver.earnings" },
  { href: "/driver/cities", textId: "footer.driver.cities" },
  { href: "https://www.lyft.com/click/dax-help-center", textId: "footer.driver.help" },
  { href: "/driver/safety", textId: "footer.driver.safety" },
  { href: "/driver-application-requirements", textId: "footer.driver.applicationRequirements" },
  { href: "/expressdrive", textId: "footer.driver.expressDrive" },
  { href: "/driver/bonus", textId: "footer.driver.bonus" },
  { href: "/driver/rewards", textId: "footer.driver.rewards" },
  { href: "/driver/insurance", textId: "footer.driver.insurance" },
  { href: "/black-car-fleet", textId: "footer.driver.blackCarFleet" },
  { href: "/hub", textId: "footer.driver.driverBlog" },
  { href: "/driver/beta", textId: "footer.driver.betaProgram" },
];

export const footerRiderLinks = [
  { href: "/rider", textId: "footer.rider.signup" },
  { href: "https://www.lyft.com/memberships/lyft-pink-all-access-annual/offer-details", textId: "footer.rider.pink" },
  { href: "/rider/cities", textId: "footer.rider.cities" },
  { href: "https://www.lyft.com/click/pax-help-center", textId: "footer.rider.help" },
  { href: "/rider/safety", textId: "footer.rider.safety" },
  { href: "/rider/business", textId: "footer.rider.businessProfile" },
  { href: "/rider/rewards", textId: "footer.rider.rewards" },
  { href: "/events", textId: "footer.rider.events" },
  { href: "/rider/airports", textId: "footer.rider.airports" },
  { href: "/gift", textId: "footer.rider.giftCards" },
  { href: "/donate", textId: "footer.rider.donate" },
];

export const footerLyftLinks = [
  { href: "/careers", textId: "footer.metuaa.careers" },
  { href: "/lyftup/programs", textId: "footer.metuaa.metuaaUp" },
  { href: "/business", textId: "footer.metuaa.companies" },
  { href: "/healthcare", textId: "footer.metuaa.healthcare" },
  { href: "/bikes", textId: "footer.metuaa.bikes" },
  { href: "/scooters", textId: "footer.metuaa.scooters" },
  { href: "/autonome", textId: "footer.metuaa.autonomous" },
  { href: "https://ads.lyft.com", textId: "footer.metuaa.ads" },
  { href: "/lieux-et-evenements", textId: "footer.metuaa.venuePartnerships" },
  { href: "/developers", textId: "footer.metuaa.developers" },
  { href: "/blog", textId: "footer.metuaa.blog" },
  { href: "/press", textId: "footer.metuaa.press" },
  { href: "https://investor.lyft.com", textId: "footer.metuaa.investorRelations" },
];

export const footerSocialLinks = [
  { href: "https://twitter.com/lyft", labelId: "footer.social.twitter", Icon: IconTwitter },
  { href: "https://www.facebook.com/lyft", labelId: "footer.social.facebook", Icon: IconFacebook },
  { href: "https://www.instagram.com/lyft", labelId: "footer.social.instagram", Icon: IconInstagram },
];

export const footerLegalLinks = [
  { href: "/terms", textId: "footer.legal.terms" },
  { href: "/privacy", textId: "footer.legal.privacy" },
  { href: "/accessibility", textId: "footer.legal.accessibility" },
  {
    href: "/privacy/sharing",
    textId: "footer.legal.privacyChoices",
    Icon: IconPrivacyChoice,
  },
];

export { logo };
